/**
 * Estado del chat del Agente TA Metrics dentro de supportIA.
 *
 * Pedido de Sol (17/09/2026): este bot NO guarda la conversación. Cada vez que
 * se abre el drawer arranca limpio — solo este bot; los demás asistentes de
 * Internal siguen persistiendo. No hay localStorage ni servidor: la
 * conversación vive en memoria mientras el drawer está abierto y se descarta al
 * cerrarlo (es información sensible de TA: recruiters, Hiring Managers).
 *
 * Sigue viviendo fuera del componente por una sola razón: si la persona cierra
 * el drawer mientras el agente calcula (10-20 s), la respuesta que llega
 * después no tiene que caer en la sesión nueva. Cada apertura es una `session`;
 * una escritura de una sesión vieja se descarta.
 */

import type { TaAskAttachment } from '@/lib/ta-api';

export interface TaChatMessage {
  role: 'user' | 'assistant';
  text: string;
  /** §18: el PDF de status se ofrece como botón, no como link en el texto. */
  attachment?: TaAskAttachment;
  meta?: { latencyMs: number; downloadedAt: string; hoy: string; issues: string[] };
}

export interface TaChatState {
  messages: TaChatMessage[];
  pending: boolean;
}

const MAX_MESSAGES = 30;
const listeners = new Set<() => void>();
let session = 0;
let state: TaChatState = { messages: [], pending: false };

/** Clave del historial que se guardaba en localStorage hasta el 17/09: se limpia al abrir. */
const legacyStorageKey = (email: string | undefined) => `ta-assistant-messages:${(email ?? 'anon').toLowerCase()}`;

function emit() { for (const l of listeners) l(); }

/**
 * Nueva sesión: chat vacío. Se llama al montar el drawer (una vez por apertura).
 * No emite: se llama durante el render del componente, que lee el snapshot
 * justo después. Devuelve el id de sesión que firma las escrituras.
 */
export function resetTaChat(email: string | undefined): number {
  session += 1;
  state = { messages: [], pending: false };
  try { localStorage.removeItem(legacyStorageKey(email)); } catch { /* modo privado */ }
  return session;
}

export function getTaChatSession(): number { return session; }

export function subscribeTaChat(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function getTaChatSnapshot(): TaChatState { return state; }

/** Escrituras firmadas con la sesión que preguntó: si el drawer se cerró y reabrió, se descartan. */
export function setTaChatMessages(sess: number, messages: TaChatMessage[]): void {
  if (sess !== session) return;
  state = { ...state, messages: messages.slice(-MAX_MESSAGES) };
  emit();
}

export function setTaChatPending(sess: number, pending: boolean): void {
  if (sess !== session || state.pending === pending) return;
  state = { ...state, pending };
  emit();
}
